import { checkSearchField } from 'designer/component/action/Search'
import { saveStorage } from 'designer/component/action/Storage'
import objectPath from 'object-path'
import {
  onUpdateCondition,
  searchCondition,
} from 'designer/component/action/condition/Action'
import { hasRules, onUpdateRules } from 'designer/component/rules/Action'
import { debounceCallback } from 'utility/debounceCallback.util'
import { getDefaultValueFromUi } from 'designer/component/action/Data'
import { getConditionRuleJsonForm } from 'designer/component/action/condition/FormComponentCondition.util'

const getUiKey = ({ designContext, config, uischema, setLabel }) => {
  if (!uischema) return
  const { designerProps } = designContext
  const { isDynamic = false } = config
  if (isDynamic) {
    if (designerProps) {
      let elementName = designerProps.filter(
        ({ propType }) => propType === 'name',
      )
      let uiName = elementName.find(({ prop }) => `@${prop}` === uischema.name)
      if (uiName) {
        setLabel(uiName.systemValue)
        return uiName.systemName
      }
    }
    return uischema.name
  }
  return uischema.key
}

const getUiName = ({ designContext, config, uischema, setLabel }) => {
  if (!uischema) return
  const { designerProps } = designContext
  const { isDynamic = false, isArray, arrayName } = config
  if (isDynamic) {
    if (designerProps) {
      let elementName = designerProps.filter(
        ({ propType }) => propType === 'name',
      )
      let uiName = elementName.find(({ prop }) => `@${prop}` === uischema.name)
      if (uiName) {
        setLabel(uiName.systemValue)
        return uiName.systemName
      }
    }
  }
  if (isArray) {
    return `@${arrayName}.${uischema.name}`
  }
  return uischema.name
}

const getData = (isSearch, props) => {
  const { designContext, config } = props
  const { searchData = {}, jsonForm } = designContext
  const {
    isDynamic = false,
    dynamicData = {},
    isFormComponent,
    formComponent = {},
  } = config
  const formData = jsonForm.formData
  if (isDynamic) return dynamicData
  if (isSearch) return searchData
  if (isFormComponent) {
    let { name = '' } = formComponent
    return objectPath.get(formData, name)
  }
  return formData
}

const getRuleDesignContext = ({ designContext, config }) => {
  const { jsonForm, isUiChanged } = designContext
  const ruleJsonForm = getConditionRuleJsonForm({
    jsonForm,
    isUiChanged,
    config,
  })

  if (ruleJsonForm === jsonForm) return designContext

  return {
    ...designContext,
    jsonForm: ruleJsonForm,
  }
}

const isPrimitiveValue = (value) =>
  value !== undefined &&
  value !== null &&
  value !== '' &&
  ['string', 'number', 'boolean'].includes(typeof value)

const getResponseItem = (response = {}) =>
  objectPath.get(response, 'data') || response

const getResponseMatchScore = (data = {}, item = {}) => {
  return Object.keys(data).reduce((score, key) => {
    if (!key || key.startsWith('@')) return score

    let value = objectPath.get(data, key)
    if (!isPrimitiveValue(value)) return score

    let responseValue = objectPath.get(item, key)
    if (!isPrimitiveValue(responseValue)) return score

    return `${responseValue}` === `${value}` ? score + 1 : score
  }, 0)
}

const getResponseFieldValue = (data = {}, uiName) => {
  if (!uiName) return

  let responseData = objectPath.get(data, '@response') || {}
  let responseList = Object.values(responseData)
  let foundResponse = responseList
    .map((response) => {
      let item = getResponseItem(response)
      let value = objectPath.get(item, uiName)

      return {
        score: value ? getResponseMatchScore(data, item) : 0,
        value,
      }
    })
    .filter(({ score, value }) => score > 0 && value)
    .sort((a, b) => b.score - a.score)[0]

  return foundResponse?.value
}

const getRuleMatchKeys = ({
  isDynamic,
  dynamicName,
  dynamicKey,
  dynamicIndex,
  uiKey,
  uiName,
  valueKey,
  valueName,
}) => {
  const addKey = (keys = [], key) => {
    if (!key) return keys
    if (keys.includes(key)) return keys

    keys.push(key)
    return keys
  }
  const addShortKey = (keys = [], key = '') => {
    let isScopedKey = String(key || '')
      .split('.')
      .some((item) => item.startsWith('@'))
    if (isScopedKey) return keys

    let shortKey = String(key || '')
      .split('.')
      .filter((item) => item)
      .pop()

    return addKey(keys, shortKey)
  }
  let keys = []

  ;[valueKey, valueName, uiKey, uiName].forEach((key) => {
    addKey(keys, key)
    addShortKey(keys, key)
  })

  if (isDynamic) {
    let dynamicKeys = [dynamicName, dynamicKey].filter(
      (key, index, list) => key && list.indexOf(key) === index,
    )
    let fieldKeys = [uiKey, uiName].filter(
      (key, index, list) => key && list.indexOf(key) === index,
    )

    dynamicKeys.forEach((dynamicKey) => {
      fieldKeys.forEach((fieldKey) => {
        addKey(keys, `${dynamicKey}.${dynamicIndex}.${fieldKey}`)
        addKey(keys, `${dynamicKey}.${fieldKey}`)
      })
    })
  }

  return keys
}

const mergeRuleUiKeys = (keys = [], extraKeys = []) => {
  let ruleUiKeys = Array.isArray(extraKeys) ? extraKeys : [extraKeys]

  return [...keys, ...ruleUiKeys].filter(
    (key, index, list) => key && list.indexOf(key) === index,
  )
}

export const getFieldProps = (props) => {
  const { designContext, uischema, auth } = props
  const { jsonForm, isUiChanged, widgetName } = designContext
  const uiKey = getUiKey(props)
  const uiName = getUiName(props)
  const uiInputKey = getUiKey({ ...props, uischema: uischema?.input })
  const uiInputName = getUiName({ ...props, uischema: uischema?.input })

  const isSearch = checkSearchField(jsonForm, uiKey)
  const data = getData(isSearch, props)
  const keyValue = objectPath.get(data, uiKey)
  const nameValue = objectPath.get(data, uiName)
  const hasFieldValue =
    objectPath.has(data, uiKey) || objectPath.has(data, uiName)
  const responseValue = getResponseFieldValue(data, uiName)
  const value =
    keyValue === false || keyValue === 0
      ? keyValue
      : keyValue || nameValue || (!hasFieldValue ? responseValue : undefined)
  const defaultValue = getDefaultValueFromUi(
    uischema,
    uischema.key,
    jsonForm,
    auth,
    'defaultValue',
  )
  const defaultChecked = getDefaultValueFromUi(
    uischema,
    uischema.key,
    jsonForm,
    auth,
    'defaultChecked',
  )
  const defaultInput = getDefaultValueFromUi(
    uischema?.input,
    uischema?.input?.key,
    jsonForm,
    auth,
    'defaultValue',
  )
  const mapResponse = objectPath.get(data, ['@mapResponse', uiKey])
  const renderWidget = objectPath.get(isUiChanged, [widgetName, uischema.name])
  return {
    uiKey,
    uiName,
    uiInputKey,
    uiInputName,
    value,
    data,
    isSearch,
    defaultValue,
    defaultChecked,
    defaultInput,
    renderWidget,
    mapResponse,
  }
}

export const updateFieldValue = ({
  props,
  designContext,
  fieldProps,
  value,
  name,
  isOnChange = false,
  isSkipTabForm,
  skipSearch = false,
  ruleUiKeys = [],
}) => {
  const { config, uischema } = props
  const { uiKey, uiName, isSearch } = fieldProps
  const {
    jsonForm,
    setFieldFormData,
    setFieldSearchData,
    setFieldAllData,
    setIsInputSearch,
    isUiChanged,
    setIsUiChanged,
  } = designContext
  const {
    isDynamic = false,
    dynamicName = '',
    dynamicKey = '',
    dynamicIndex = 0,
    dynamicData = {},
    fullDynamicKey = '',
    onUpdateFormData = () => {},
    isArray,
    arrayName,
    arrayKey,
    viewKey,
    dynamicConfig,
    isFormComponent,
    formComponent = {},
  } = config
  if (isFormComponent && !isSkipTabForm) {
    let formName = formComponent.name
    return updateFieldValue({
      props,
      designContext,
      fieldProps,
      value,
      name: formName ? `${formName}.${name ?? uiName}` : `${name ?? uiName}`,
      isOnChange,
      isSkipTabForm: true,
      skipSearch,
      ruleUiKeys,
    })
  }
  let valueKey
  let valueName
  if (viewKey) {
    let formData = jsonForm.formData
    let viewDynamicKey
    if (dynamicConfig.dynamicKey) {
      viewDynamicKey = `${viewKey}.${dynamicConfig.dynamicName}.${dynamicConfig.dynamicIndex}.${dynamicName}.${dynamicIndex}`
    } else {
      viewDynamicKey = `${viewKey}.${dynamicName}.${dynamicIndex}`
    }
    let splitKey = uiKey.split('.')
    if (splitKey.length > 1) {
      let lastKey = splitKey.pop()
      let fullKey = viewDynamicKey
      splitKey.forEach((key) => {
        fullKey = `${fullKey}.${key}`
        let hasKey = objectPath.get(formData, fullKey)
        if (!hasKey) {
          objectPath.set(formData, fullKey, {})
        }
      })
      valueKey = `${fullKey}.${lastKey}`
    } else {
      valueKey = `${viewDynamicKey}.${name ?? uiKey}`
    }
  } else if (isDynamic) {
    let key = name ?? uiKey
    let keys = key.split('.')
    if (keys.length > 1) {
      let nextKey = []
      keys.forEach((key, index) => {
        const isLast = index === keys.length - 1
        nextKey.push(key)
        if (isLast) {
          objectPath.set(dynamicData, nextKey, value)
        } else {
          let objectValue = objectPath.get(dynamicData, nextKey)
          if (objectValue === null || objectValue === undefined) {
            objectPath.set(dynamicData, nextKey, {})
          }
        }
      })
    }
    objectPath.set(dynamicData, key, value)
    valueKey = fullDynamicKey
    value = dynamicData
  } else {
    valueKey = name ?? uiKey
    valueName = name ?? uiName
  }
  if (isSearch && !skipSearch) {
    setFieldSearchData(uischema, valueKey, value, { isSearch })
    debounceCallback(setIsInputSearch, [valueKey], 'onInputUpdate')
    if (valueName) {
      setFieldSearchData(uischema, valueName, value, { isSearch })
    }
  }

  setFieldFormData(valueKey, value)
  setFieldAllData(valueKey, value)
  onUpdateFormData(valueKey, value)

  if (valueName && valueName !== valueKey) {
    setFieldFormData(valueName, value)
    setFieldAllData(valueName, value)
  }
  if (isOnChange) {
    if (isArray) {
      objectPath.set(isUiChanged, `syncData.${arrayKey}`, true)
    }
    if (!skipSearch) {
      objectPath.set(isUiChanged, `onChange`, true)
    }
    if (isArray || !skipSearch) {
      setIsUiChanged(isUiChanged)
    }
    let ruleFormData = { ...(jsonForm.formData || {}) }
    objectPath.set(ruleFormData, valueKey, value)
    if (valueName && valueName !== valueKey) {
      objectPath.set(ruleFormData, valueName, value)
    }
    let valueChangeUiKeys = getRuleMatchKeys({
      isDynamic,
      dynamicName,
      dynamicKey,
      dynamicIndex,
      uiKey,
      uiName,
      valueKey,
      valueName,
    })

    onUpdateRules(
      props,
      getRuleDesignContext({ designContext, config }),
      [valueKey, valueName],
      ruleFormData,
      {
        isOnChange,
        valueChange: value,
        eventKey: 'onChange',
        ruleUiKeys: mergeRuleUiKeys(valueChangeUiKeys, ruleUiKeys),
        valueChangeUiKeys,
        ...(isDynamic
          ? {
              isDynamic,
              name: uiKey,
              dynamicName,
              dynamicIndex,
            }
          : {}),
        ...(formComponent?.name
          ? {
              formComponentName: formComponent.name,
            }
          : {}),
        ...(isArray ? { isArray, arrayName, arrayKey } : {}),
      },
    )
  }
  saveStorage(jsonForm, uischema, name ?? uiName, value)
}

export const updateCheckboxInput = ({
  props,
  designContext,
  fieldProps,
  value,
  uiKey,
  uiName,
  isOnChange = false,
  isSkipTabForm,
}) => {
  const { config, uischema } = props
  const { isSearch } = fieldProps
  const {
    jsonForm,
    setFieldFormData,
    setFieldSearchData,
    setFieldAllData,
    setIsInputSearch,
    isUiChanged,
    setIsUiChanged,
  } = designContext
  const {
    isDynamic = false,
    dynamicName = '',
    dynamicKey = '',
    dynamicIndex = 0,
    dynamicData = {},
    onUpdateFormData = () => {},
    isArray,
    arrayName,
    arrayKey,
    viewKey,
    dynamicConfig,
    isFormComponent,
    formComponent = {},
  } = config
  if (isFormComponent && !isSkipTabForm) {
    let formName = formComponent.name
    return updateFieldValue({
      props,
      designContext,
      fieldProps,
      value,
      name: formName ? `${formName}.${uiName}` : `${uiName}`,
      isOnChange,
      isSkipTabForm: true,
    })
  }
  let valueKey
  let valueName
  if (viewKey) {
    let formData = jsonForm.formData
    let viewDynamicKey
    if (dynamicConfig.dynamicKey) {
      viewDynamicKey = `${viewKey}.${dynamicConfig.dynamicName}.${dynamicConfig.dynamicIndex}.${dynamicName}.${dynamicIndex}`
    } else {
      viewDynamicKey = `${viewKey}.${dynamicName}.${dynamicIndex}`
    }
    let splitKey = uiKey.split('.')
    if (splitKey.length > 1) {
      let lastKey = splitKey.pop()
      let fullKey = viewDynamicKey
      splitKey.forEach((key) => {
        fullKey = `${fullKey}.${key}`
        let hasKey = objectPath.get(formData, fullKey)
        if (!hasKey) {
          objectPath.set(formData, fullKey, {})
        }
      })
      valueKey = `${fullKey}.${lastKey}`
    } else {
      valueKey = `${viewDynamicKey}.${uiKey}`
    }
  } else if (isDynamic) {
    objectPath.set(dynamicData, uiName, value)
    valueKey = `${dynamicName}.${dynamicIndex}.${uiName}`
  } else {
    valueKey = uiKey
    valueName = uiName
  }
  if (isSearch) {
    setFieldSearchData(uischema, valueKey, value, { isSearch })
    debounceCallback(setIsInputSearch, [valueKey], 'onInputUpdate')
    if (valueName) {
      setFieldSearchData(uischema, valueName, value, { isSearch })
    }
  }
  setFieldFormData(valueKey, value)
  setFieldAllData(valueKey, value)
  onUpdateFormData(valueKey, value)
  if (valueName && valueName !== valueKey) {
    setFieldFormData(valueName, value)
    setFieldAllData(valueName, value)
  }
  if (isOnChange) {
    if (isArray) {
      objectPath.set(isUiChanged, `syncData.${arrayKey}`, true)
      setIsUiChanged(isUiChanged)
    }
    onUpdateRules(
      props,
      getRuleDesignContext({ designContext, config }),
      [valueKey, valueName],
      jsonForm.formData,
      {
        isOnChange,
        valueChange: value,
        eventKey: 'onChange',
        ruleUiKeys: getRuleMatchKeys({
          isDynamic,
          dynamicName,
          dynamicKey,
          dynamicIndex,
          uiKey,
          uiName,
          valueKey,
          valueName,
        }),
        ...(isDynamic
          ? {
              isDynamic,
              name: uiKey,
              dynamicName,
              dynamicIndex,
            }
          : {}),
        ...(isArray ? { isArray, arrayName, arrayKey } : {}),
      },
    )
  }
  saveStorage(jsonForm, uischema, uiName, value)
}

export const updateOnChange = ({
  props,
  designContext,
  fieldProps,
  isOnChange = false,
  setIsOnChange,
  value,
}) => {
  const { config, uischema } = props
  const { uiKey, uiName, data } = fieldProps
  const { isUiChanged, jsonForm } = designContext
  const {
    isDynamic = false,
    dynamicName = '',
    dynamicKey = '',
    dynamicIndex = 0,
    isArray,
    arrayName,
    arrayKey,
  } = config

  if (isOnChange) {
    let conditionJsonForm = getConditionRuleJsonForm({
      jsonForm,
      isUiChanged,
      config,
    })
    let isHasRules = hasRules(conditionJsonForm)
    let getSyncDataCondition = (uiKey) =>
      isHasRules
        ? searchCondition(conditionJsonForm, uiKey, { actionKey: 'syncData' })
        : null
    let hasSyncDataCondition = (filterCondition) =>
      !isHasRules || filterCondition.length > 0

    if (isHasRules) {
      setIsOnChange(false)
    }

    if (arrayKey) {
      let syncData = objectPath.get(isUiChanged, 'syncData') || {}
      Object.keys(syncData).forEach((arrayKey) => {
        let uiKey = [
          arrayKey,
          arrayName,
          arrayName ? `@${arrayName}` : '',
          fieldProps.uiKey,
          fieldProps.uiName,
        ]
        let filterCondition = getSyncDataCondition(uiKey)
        if (!hasSyncDataCondition(filterCondition)) return

        debounceCallback(
          onUpdateCondition,
          [
            props,
            designContext,
            uiKey,
            data,
            {
              isOnChange,
              isArray,
              arrayName,
              arrayKey,
              ...(filterCondition ? { filterCondition } : {}),
            },
          ],
          `onInputUpdateArrayCondition-${arrayKey}`,
        )
      })
    }
    if (isHasRules && isDynamic) {
      let ruleUiKeys = getRuleMatchKeys({
        isDynamic,
        dynamicName,
        dynamicKey,
        dynamicIndex,
        uiKey,
        uiName,
        valueKey: uiKey,
        valueName: uiName,
      })
      let status = {
        isOnChange,
        valueChange: value,
        ruleUiKeys,
        isDynamic,
        name: uiKey,
        dynamicName,
        dynamicKey,
        dynamicIndex,
      }
      let uiFilterCondition = getSyncDataCondition(uischema.key)
      let dynamicFilterCondition = getSyncDataCondition(dynamicKey)
      if (
        hasSyncDataCondition(uiFilterCondition) ||
        hasSyncDataCondition(dynamicFilterCondition)
      ) {
        let uiStatus = {
          ...status,
          ...(uiFilterCondition ? { filterCondition: uiFilterCondition } : {}),
        }
        let dynamicStatus = {
          ...status,
          ...(dynamicFilterCondition
            ? { filterCondition: dynamicFilterCondition }
            : {}),
        }

        if (hasSyncDataCondition(uiFilterCondition)) {
          debounceCallback(
            onUpdateCondition,
            [props, designContext, uischema.key, data, uiStatus],
            `onInputUpdateCondition-${dynamicKey}-${dynamicIndex}`,
          )
        }
        if (hasSyncDataCondition(dynamicFilterCondition)) {
          debounceCallback(
            onUpdateCondition,
            [props, designContext, dynamicKey, '', dynamicStatus],
            `onInputUpdateDynamicCondition-${dynamicKey}-${dynamicIndex}`,
          )
        }
      }
    }
    if (isHasRules) return
    if (isDynamic) {
      let ruleUiKeys = getRuleMatchKeys({
        isDynamic,
        dynamicName,
        dynamicKey,
        dynamicIndex,
        uiKey,
        uiName,
        valueKey: uiKey,
        valueName: uiName,
      })
      let status = {
        isOnChange,
        valueChange: value,
        ruleUiKeys,
        isDynamic,
        name: uiKey,
        dynamicName,
        dynamicKey,
        dynamicIndex,
      }
      debounceCallback(
        onUpdateCondition,
        [props, designContext, uischema.key, data, status],
        `onInputUpdateCondition-${dynamicKey}-${dynamicIndex}`,
      )
      debounceCallback(
        onUpdateCondition,
        [props, designContext, dynamicKey, '', status],
        `onInputUpdateDynamicCondition-${dynamicKey}-${dynamicIndex}`,
      )
    } else {
      debounceCallback(
        onUpdateCondition,
        [
          props,
          designContext,
          uiKey,
          data,
          {
            isOnChange,
            valueChange: value,
            ruleUiKeys: getRuleMatchKeys({
              uiKey,
              uiName,
              valueKey: uiKey,
              valueName: uiName,
            }),
          },
        ],
        'onInputUpdateCondition',
      )
    }
    setIsOnChange(false)
  }
}

export const updateWidgetData = (dataProps) => {
  const { props, designContext } = dataProps
  const { uischema, isOnChange } = props
  let { isUiChanged, setIsUiChanged, widgetName } = designContext
  let renderWidget = objectPath.get(isUiChanged, [widgetName, uischema.name])
  if (widgetName && renderWidget) {
    let data = objectPath.get(isUiChanged, [widgetName, '@data'])
    if (data) {
      let value = objectPath.get(data, uischema.name)
      updateFieldValue({ ...dataProps, value, isOnChange })
    }
    objectPath.del(isUiChanged, [widgetName, uischema.name])
    setIsUiChanged(isUiChanged)
  }
}

export const updateMapResponse = (dataProps) => {
  const { props, designContext, fieldProps } = dataProps
  const { jsonForm, setFormData } = designContext
  const { uischema } = props
  const { uiKey, data } = fieldProps
  const formData = jsonForm.formData || {}
  if (!hasRules(jsonForm)) {
    onUpdateCondition(props, designContext, uiKey, data)
  }
  objectPath.del(formData, ['@mapResponse', uischema.key])
  setFormData(formData)
}
